import React, { useState, useRef, useEffect } from 'react';

const ComboboxWithSuggestions = ({
  value,
  onChange,
  suggestions = [],
  placeholder = "Seleccione o escriba...",
  disabled = false,
  loading = false,
  error = false,
  maxLength = 50,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);
  const comboboxRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (inputValue.trim() === "") {
      setFilteredSuggestions(suggestions);
    } else {
      const filtered = suggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredSuggestions(filtered);
    }
  }, [inputValue, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const limitedValue = newValue.slice(0, maxLength);
    setInputValue(limitedValue);
    onChange(limitedValue);
    setIsOpen(true);
  };

  const handleSelectSuggestion = (suggestion) => {
    const limitedSuggestion = suggestion.slice(0, maxLength);
    setInputValue(limitedSuggestion);
    onChange(limitedSuggestion);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-full" ref={comboboxRef}>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        maxLength={maxLength}
        className={`w-full outline-none border font-itcbook rounded px-2 py-1 text-sm ${
          error 
            ? 'border-red-300 bg-red-50' 
            : 'border-gray-300'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        placeholder={required && placeholder ? `${placeholder} *` : placeholder}
        disabled={disabled}
      />


      
      <div 
        className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg max-h-60">
          {loading ? (
            <div className="px-4 py-2 text-xs text-gray-500 font-itcbook">
              Cargando sugerencias...
            </div>
          ) : filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 font-itcbook"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                {suggestion}
              </button>
            ))
          ) : (
            <div className="px-4 py-2 text-xs text-gray-500 font-itcbook">
              {inputValue.trim() === "" 
                ? "No hay sugerencias disponibles" 
                : "No se encontraron coincidencias"}
            </div>
          )}
          
          {inputValue.trim() !== "" && !filteredSuggestions.includes(inputValue) && (
            <button
              type="button"
              className="block w-full px-4 py-2 text-xs text-left text-blue-600 border-t border-gray-200 hover:bg-gray-100 font-itcbook"
              onClick={() => handleSelectSuggestion(inputValue)}
            >
              Usar "{inputValue}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ComboboxWithSuggestions;